import React, { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Box, Text, VStack } from '@gluestack-ui/themed';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ChartAxisX, ChartPoint } from '@/admin/dashboards/chartViewModel';

import { scoreChartYDomain, scoreChartYTicks } from './scoreChartDomain';
import { revisingCountAxis } from './scoreRevisingAxis';
import { useChartContainerWidth } from './useChartContainerWidth';
import type { ChartTheme } from './useChartTheme';
import { DASHBOARD_MOTION, DASHBOARD_RECHARTS_EASE } from './dashboardMotion';

export const SCORE_CHART_HEIGHT_PX = 300;
/** Right margin sized for the Pass chip (viewBox.width + offset + chip width). */
export const SCORE_CHART_MARGIN_RIGHT = 52;
export const SCORE_PASS_LABEL_CHIP_WIDTH = 38;
export const SCORE_PASS_LABEL_CHIP_OFFSET = 8;
/** Qualities: Pass chip is 11px Inter 600. SVG text does not inherit the page font. */
export const SCORE_PASS_LABEL_FONT_FAMILY = 'Inter-SemiBold, Inter, system-ui, sans-serif';
export const SCORE_PASS_LABEL_FONT_SIZE = 11;
export const SCORE_PASS_LABEL_FONT_WEIGHT = 600;

export const SCORE_MEDIAN_GRADIENT_START = '#60A5FA';
export const SCORE_MEDIAN_GRADIENT_END = '#2563EB';
export const SCORE_BAND_COLOR = '#3B82F6';
/** Chart band is this blue at 5–20% opacity. The swatch is stronger so a 14px chip still reads as that blue. */
export const SCORE_BAND_LEGEND_FILL = '#3B82F666';
const SCORE_PASS_LINE_STROKE = '#98A2B3';
/** Still-revising bars: neutral grey, faint, so they never read as a score series. */
export const SCORE_REVISING_BAR_FILL = '#98A2B3';
export const SCORE_REVISING_BAR_OPACITY = 0.22;
export const SCORE_REVISING_LEGEND_FILL = '#98A2B359';
const SCORE_REVISING_TICK_FILL = '#98A2B3';
const SCORE_REVISING_LABEL_FILL = '#667085';
/** Count ticks start past the Pass chip (Recharts adds its 6px tick size before this margin). */
export const SCORE_REVISING_AXIS_TICK_MARGIN = 48;
export const SCORE_REVISING_AXIS_WIDTH = 100;

export type TrendChartRow = {
  xLabel: string;
  value: number | null;
  p20: number | null;
  p80: number | null;
  bandBase: number;
  bandSpan: number;
  n: number | null;
  parts: Record<string, number> | null;
  nRevising: number | null;
};

export type TrendChartChartProps = {
  points: ChartPoint[] | null | undefined;
  theme: ChartTheme;
  formatX: (x: ChartAxisX) => string;
  /** 100 when gated (% of pass mark + Pass line); null when rubric % only. */
  scoreThreshold: number | null;
  testID?: string;
  /** Dom tests may set an explicit width when ticks are needed without layout. */
  layoutWidth?: number;
  chartAnimationActive?: boolean;
  chartMountKey?: string;
};

function normalizedParts(parts: ChartPoint['parts']): Record<string, number> | null {
  if (!parts || typeof parts !== 'object') return null;
  return Object.keys(parts).length ? parts : null;
}

export function hasPlottableScoreValues(points: ChartPoint[] | null | undefined): boolean {
  if (!points?.length) return false;
  return points.some(
    (point) => typeof point.value === 'number'
      || typeof point.p20 === 'number'
      || typeof point.p80 === 'number',
  );
}

export function buildTrendChartRows(
  points: ChartPoint[] | null | undefined,
  formatX: (x: ChartAxisX) => string,
): TrendChartRow[] {
  if (!points?.length) return [];
  return points.map((point) => {
    const p20 = point.p20 ?? null;
    const p80 = point.p80 ?? null;
    const bandBase = p20 ?? 0;
    const bandSpan = p20 != null && p80 != null ? Math.max(0, p80 - p20) : 0;
    return {
      xLabel: formatX(point.x),
      value: point.value ?? null,
      p20,
      p80,
      bandBase,
      bandSpan,
      n: point.n ?? null,
      parts: normalizedParts(point.parts),
      nRevising: point.nRevising ?? null,
    };
  });
}

function sampleLine(row: TrendChartRow, t: TFunction): string {
  const n = row.n ?? 0;
  if (row.nRevising == null) {
    return t('admin.analytics.tooltip.sampleSize', { n });
  }
  return t('admin.analytics.scoreProgression.tooltipRevising', {
    revising: row.nRevising,
    n,
    done: Math.max(0, n - row.nRevising),
  });
}

export function ScoreTrendTooltipBody({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: TrendChartRow }>;
}) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  const partEntries = row.parts
    ? Object.entries(row.parts).sort(([a], [b]) => Number(a) - Number(b))
    : [];

  return (
    <Box testID="score-trend-tooltip" bg="$bgCard" p="$2" borderRadius="$md">
      <Text testID="score-trend-tooltip-n" size="xs">
        {sampleLine(row, t)}
      </Text>
      <Box
        testID="score-trend-tooltip-parts-scroll"
        style={{ maxHeight: 240, overflow: 'scroll' }}
      >
        {partEntries.map(([score, count]) => (
          <Text
            key={score}
            testID={`score-trend-tooltip-part-${score}`}
            size="xs"
          >
            {t('admin.analytics.scoreProgression.tooltipScoreLine', {
              score,
              count: Math.round(count),
            })}
          </Text>
        ))}
      </Box>
    </Box>
  );
}

function cartesianBox(viewBox: object | undefined): { x: number; y: number; width: number } | null {
  if (!viewBox || !('x' in viewBox)) return null;
  const box = viewBox as { x?: number; y?: number; width?: number };
  return { x: box.x ?? 0, y: box.y ?? 0, width: box.width ?? 0 };
}

function ScorePassReferenceLabel({
  viewBox,
  labelText,
}: {
  viewBox?: object;
  labelText: string;
}) {
  const box = cartesianBox(viewBox);
  if (!box) return null;
  const { x, y, width } = box;
  return (
    <g transform={`translate(${x + width + SCORE_PASS_LABEL_CHIP_OFFSET}, ${y - 10})`}>
      <rect
        width={SCORE_PASS_LABEL_CHIP_WIDTH}
        height={20}
        rx={6}
        fill="#ffffff"
        stroke="#EAECF0"
      />
      <text
        {...({ testID: 'score-pass-label' } as React.SVGProps<SVGTextElement>)}
        x={19}
        y={14}
        textAnchor="middle"
        fontSize={SCORE_PASS_LABEL_FONT_SIZE}
        fontWeight={SCORE_PASS_LABEL_FONT_WEIGHT}
        fontFamily={SCORE_PASS_LABEL_FONT_FAMILY}
        fill="#667085"
      >
        {labelText}
      </text>
    </g>
  );
}

function RevisingAxisTitle({
  viewBox,
  text,
  fontFamily,
}: {
  viewBox?: object;
  text: string;
  fontFamily: string;
}) {
  if (!viewBox || !('x' in viewBox)) return null;
  const box = viewBox as { x?: number; y?: number; width?: number; height?: number };
  const x = (box.x ?? 0) + (box.width ?? 0) - 14;
  const y = (box.y ?? 0) + (box.height ?? 0) / 2;
  return (
    <text
      x={x}
      y={y}
      transform={`rotate(90, ${x}, ${y})`}
      textAnchor="middle"
      fontSize={11}
      fontFamily={fontFamily}
      fill={SCORE_REVISING_LABEL_FILL}
    >
      {text}
    </text>
  );
}

/**
 * Score progression line with p20–p80 band (no release lines). By attempt number, grey bars on a
 * right-hand count axis show how many sessions still submitted that attempt.
 */
export default function TrendChartChart({
  points,
  theme,
  formatX,
  scoreThreshold,
  testID = 'trend-chart',
  layoutWidth,
  chartAnimationActive = true,
  chartMountKey = 'score-chart',
}: TrendChartChartProps) {
  const { t } = useTranslation();
  const gradientUid = useId().replace(/:/g, '');
  const bandGradientId = `score-band-fill-${gradientUid}`;
  const medianGradientId = `score-median-stroke-${gradientUid}`;

  const plottable = useMemo(() => hasPlottableScoreValues(points), [points]);
  const rows = useMemo(
    () => (plottable ? buildTrendChartRows(points, formatX) : []),
    [plottable, points, formatX],
  );
  const yDomain = useMemo(
    () => (plottable ? scoreChartYDomain(points) : [20, 100] as [number, number]),
    [plottable, points],
  );
  const yTicks = useMemo(() => scoreChartYTicks(yDomain), [yDomain]);
  const countAxis = useMemo(() => (plottable ? revisingCountAxis(points) : null), [plottable, points]);

  const showPassLine = plottable && scoreThreshold === 100;
  const axisTitleKey = scoreThreshold === 100
    ? 'admin.analytics.scoreProgression.axisLabel'
    : 'admin.analytics.scoreProgression.axisLabelRubric';
  const passLabelText = t('admin.analytics.scoreProgression.passLine');

  const { chartWidth, chartHeight, onLayout } = useChartContainerWidth(
    layoutWidth,
    SCORE_CHART_HEIGHT_PX,
  );

  return (
    <Box testID={testID} width="100%" onLayout={onLayout}>
      {plottable && rows.length > 0 && chartWidth > 0 && (
        <ComposedChart
          key={chartMountKey}
          width={chartWidth}
          height={chartHeight}
          data={rows}
          margin={{
            top: 8, right: countAxis ? 0 : SCORE_CHART_MARGIN_RIGHT, left: 0, bottom: 0,
          }}
        >
          <defs>
            <linearGradient id={bandGradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={SCORE_BAND_COLOR} stopOpacity={0.2} />
              <stop offset="100%" stopColor={SCORE_BAND_COLOR} stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id={medianGradientId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={SCORE_MEDIAN_GRADIENT_START} />
              <stop offset="100%" stopColor={SCORE_MEDIAN_GRADIENT_END} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={theme.grid} vertical={false} />
          <XAxis
            dataKey="xLabel"
            tick={{ fill: theme.axis, fontSize: 11, fontFamily: theme.fontFamily }}
            axisLine={{ stroke: theme.grid }}
            tickLine={false}
          />
          <YAxis
            domain={yDomain}
            ticks={yTicks}
            interval={0}
            allowDataOverflow
            tick={{ fill: theme.axis, fontSize: 11, fontFamily: theme.fontFamily }}
            axisLine={{ stroke: theme.grid }}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
            label={{
              value: t(axisTitleKey),
              angle: -90,
              position: 'insideLeft',
              // insideLeft anchors the string at its start, so a -90° title hangs off the plot midpoint.
              textAnchor: 'middle',
              style: { fill: theme.axis, fontSize: 11, fontFamily: theme.fontFamily },
            }}
          />
          {countAxis && (
            <YAxis
              yAxisId="count"
              orientation="right"
              domain={countAxis.domain}
              ticks={countAxis.ticks}
              interval={0}
              allowDecimals={false}
              width={SCORE_REVISING_AXIS_WIDTH}
              tickMargin={SCORE_REVISING_AXIS_TICK_MARGIN}
              tick={{ fill: SCORE_REVISING_TICK_FILL, fontSize: 11, fontFamily: theme.fontFamily }}
              axisLine={false}
              tickLine={false}
              label={{
                value: t('admin.analytics.scoreProgression.axisRevising'),
                content: (props) => (
                  <RevisingAxisTitle
                    {...props}
                    text={t('admin.analytics.scoreProgression.axisRevising')}
                    fontFamily={theme.fontFamily}
                  />
                ),
              }}
            />
          )}
          <Tooltip
            content={<ScoreTrendTooltipBody />}
            isAnimationActive={false}
            cursor={countAxis ? { fill: '#F2F4F7', fillOpacity: 0.6 } : undefined}
          />
          {countAxis && (
            <Bar
              dataKey="nRevising"
              yAxisId="count"
              fill={SCORE_REVISING_BAR_FILL}
              fillOpacity={SCORE_REVISING_BAR_OPACITY}
              radius={[3, 3, 0, 0]}
              maxBarSize={36}
              isAnimationActive={chartAnimationActive}
              animationDuration={DASHBOARD_MOTION.scoreBandMs}
              animationEasing={DASHBOARD_RECHARTS_EASE}
            >
              <LabelList
                dataKey="nRevising"
                position="top"
                fill={SCORE_REVISING_LABEL_FILL}
                fontSize={10.5}
                fontFamily={theme.fontFamily}
              />
            </Bar>
          )}
          <Area
            type="linear"
            dataKey="bandBase"
            stackId="band"
            stroke="none"
            fill="transparent"
            isAnimationActive={chartAnimationActive}
            animationDuration={DASHBOARD_MOTION.scoreBandMs}
            animationEasing={DASHBOARD_RECHARTS_EASE}
          />
          <Area
            type="linear"
            dataKey="bandSpan"
            stackId="band"
            stroke="none"
            fill={`url(#${bandGradientId})`}
            isAnimationActive={chartAnimationActive}
            animationDuration={DASHBOARD_MOTION.scoreBandMs}
            animationEasing={DASHBOARD_RECHARTS_EASE}
          />
          {showPassLine && (
            <ReferenceLine
              y={100}
              yAxisId={0}
              stroke={SCORE_PASS_LINE_STROKE}
              strokeWidth={1}
              label={{
                value: passLabelText,
                content: (props) => (
                  <ScorePassReferenceLabel {...props} labelText={passLabelText} />
                ),
                position: 'insideTopRight',
              }}
            />
          )}
          <Line
            type="linear"
            dataKey="value"
            stroke={`url(#${medianGradientId})`}
            strokeWidth={2.5}
            dot={{
              r: 3.5,
              fill: '#ffffff',
              stroke: SCORE_BAND_COLOR,
              strokeWidth: 2,
            }}
            activeDot={false}
            isAnimationActive={chartAnimationActive}
            animationBegin={DASHBOARD_MOTION.scoreMedianBeginMs}
            animationDuration={DASHBOARD_MOTION.scoreMedianMs}
            animationEasing={DASHBOARD_RECHARTS_EASE}
            connectNulls={false}
          />
        </ComposedChart>
      )}
      {!plottable && (
        <VStack height={chartHeight} justifyContent="center" alignItems="center">
          <Text size="sm" color="$textSubtle">
            —
          </Text>
        </VStack>
      )}
    </Box>
  );
}
